/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ComponentType, type ReactElement } from 'react';
import {
    SiFacebook,
    SiInstagram,
    SiPinterest,
    SiStrava,
    SiThreads,
    SiTiktok,
    SiX,
    SiYoutube,
} from '@icons-pack/react-simple-icons';
import { cn } from '@/lib/utils';
import type { FooterSocialLink } from '@/lib/content/types';

/**
 * Closed allowlist mapping a `footer-social-connect` asset's `icon` identifier to a known icon
 * component (CRCB2B-48). An identifier absent here renders nothing rather than raising an error —
 * see the "Social icons resolve through a closed allowlist" requirement in
 * `specs/site-footer/spec.md`. Content assets cannot introduce a new glyph this way: the icon font
 * (Material Symbols Sharp, `src/components/icon/glyphs.ts`) is a separate, build-time subset and
 * these `Si*` components are their own self-contained SVGs, unrelated to that subset.
 *
 * To add a network: add an entry here. No font subset step is needed for these vector icons — that
 * step (`glyphs.ts` + `pnpm icons:subset`) only applies to the Material Symbols icon font used
 * elsewhere in the app. See `docs/README-CONTENT-ASSETS.md`.
 */
const SOCIAL_ICON_ALLOWLIST: Record<string, ComponentType<{ className?: string }>> = {
    facebook: SiFacebook,
    instagram: SiInstagram,
    youtube: SiYoutube,
    x: SiX,
    tiktok: SiTiktok,
    strava: SiStrava,
    pinterest: SiPinterest,
    threads: SiThreads,
};

/**
 * Social links in the footer's brand column, sourced from the `footer-social-connect` content asset.
 *
 * The 40px box with a 24px glyph is deliberate (Figma "Social Links", node `1808:19721`): Figma
 * draws a 40px tappable box around a 32px exported brand asset whose internal padding makes the mark
 * render ~24px optically. simple-icons glyphs are tight with no padding, so 24px here matches the
 * design's optical size — `size-8` would render visibly larger than the mockup. The 40px outer box
 * doubles as the minimum touch target.
 */
export default function SocialIcons({
    social,
    className,
}: {
    social: FooterSocialLink[];
    className?: string;
}): ReactElement {
    return (
        <div className={cn('flex items-center flex-wrap gap-4', className)}>
            {social.map((link) => {
                const Icon = SOCIAL_ICON_ALLOWLIST[link.icon];
                if (!Icon) {
                    return null;
                }
                return (
                    <a
                        key={link.url}
                        href={link.url}
                        aria-label={link.label}
                        className="inline-flex size-10 items-center justify-center text-footer-foreground hover:text-brand-gray-300 transition-colors">
                        <Icon className="size-6" />
                    </a>
                );
            })}
        </div>
    );
}
